import { presetPairs, presetFromCents, shortProductTitle, presetChoiceSteps, choiceVariants } from '#bundle-presets-state';
import {
  formatMoney, findVariant, computeTotals, componentCharges, isComplete, isMountAnswered, paramsFromSelections, mountParams,
  hydrateFromParams, hydrateMountFromParams, selectedSkiProduct, isFitPending, chargedCents, compareAtCents, parseLengthCm,
  FIT_PENDING_VALUE, BSL_MIN, BSL_MAX,
} from '#bundle-configurator-state';
import * as BundleCart from '#bundle-cart';
import { MOUNT_BY } from '#mounting-properties';
import { createTracker, publishEvent, debugEnabled, errorDetail } from '#bundle-configurator-analytics';
import { ensureBasePrices } from '#bundle-base-prices';

// Komplett-Set page (2026-10): a preset rendered as a product page. The
// customer picks the ski length; binding brake and fell size follow through
// the configurator's own fit rules (bundle-presets-state.js), the mounting
// question is the configurator's panel (configurator-mount-panel.liquid),
// and the set is bought through the configurator container with the very
// same _components payload (bundle-cart.js), so cart transform, Flow
// validation and the order look exactly like a configured set. The blob is
// the configurator's shape, reduced to one product per step
// (sections/main-product-set.liquid). Nothing here is a wizard: no steps, no
// grid, no URL rewriting beyond the chosen length and mounting answer.
//
// Sets with sizes (2026-10-24): a part on a sizeChoice step (a ski boot, a
// pole length, the trousers of a textile set) is NOT derived from the length.
// The pair prices it at its default size; the customer picks the size on its
// own pills, and the set goes into the cart only once every size is picked
// (no silent default size in a parcel). Picking a size first and the length
// second works too: this.choices keeps the picks, composeSelections lays
// them over the pair.
//
// Analytics rides the configurator's event (cfg_id = the configurator) with
// cfg_entry=set_page; no new action, no new key, so the pixel stays as it is.

const SKI_LENGTH_SELECTED = 'is-selected';

if (!customElements.get('bundle-set')) {
  customElements.define('bundle-set', class extends HTMLElement {
    connectedCallback() {
      const blob = this.querySelector('[data-configurator-data]');
      if (!blob) return; // no-JS / unavailable state stays as rendered
      try {
        this.data = JSON.parse(blob.textContent);
      } catch (error) {
        publishEvent('error', { cfg_stage: 'config' }, { debug: debugEnabled(location.search) });
        return;
      }
      if (this.data.ok === false) {
        // A preset product is in no step of its configurator (or a required
        // step has no member): the Flow validator would reject the order, so
        // the page sells nothing and says so (Liquid rendered the notice).
        publishEvent('error', { cfg_stage: 'config' }, { debug: debugEnabled(location.search) });
        return;
      }
      this.preset = this.data.preset || { products: [] };
      this.skiStep = this.preset.products[0]?.stepHandle || null;
      this.choiceSteps = presetChoiceSteps(this.data, this.preset);
      this.choices = {};
      this.selections = {};
      // An optional step the preset leaves empty (the Allrounder has no skin)
      // counts as skipped from the start: the configurator asks the customer
      // to pass such a step explicitly, the set page never shows it, so the
      // completeness walk and the hand-over link both carry the skip.
      this.skipped = new Set(this.data.steps.filter((step) => step.optional && !(step.products || []).length).map((step) => step.handle));
      this.mountChoice = null;
      this.bootSoleLength = null;
      this.bootSoleInput = '';
      this.fitToBoot = false;
      this.mountDefaulted = false;
      this.adding = false;
      try {
        this.tracker = createTracker({ data: this.data, debug: debugEnabled(location.search), currency: window.Shopify?.currency?.active, preset: this.preset.handle });
      } catch {
        this.tracker = null;
      }
      this.pairs = presetPairs(this.data, this.preset);
      // A length in the address (shared link, "Set anpassen" and back) is
      // honoured only when it is one the page can sell today.
      const params = new URLSearchParams(location.search);
      const hydrated = hydrateFromParams(this.data, params);
      const pair = this.skiStep ? this.pairs.find((p) => p.skiVariant.id === hydrated[this.skiStep]) : null;
      // Sizes in the address count when they are on offer today.
      this.choiceSteps.forEach((stepHandle) => {
        if (hydrated[stepHandle] != null && this.choiceOnOffer(stepHandle, hydrated[stepHandle])) this.choices[stepHandle] = hydrated[stepHandle];
      });
      if (pair) this.selections = this.composeSelections(pair);
      const mount = hydrateMountFromParams(this.data, params);
      this.mountChoice = mount.mountChoice;
      this.bootSoleLength = mount.bootSoleLength;
      this.bootSoleInput = mount.bootSoleLength == null ? '' : String(mount.bootSoleLength);
      this.applyMountDefault();
      this.enableControls();
      this.bindEvents();
      this.track('start', () => ({ entry: 'set_page', selections: this.selections }));
      this.renderAll();
      this.refreshBasePrices();
    }

    // Set prices outside the home country (2026-10-07), as in the
    // configurator: home prices load, then the lengths and totals re-price.
    // Until then (or after a failure) the set price stays capped at the sum
    // of its parts' shop prices.
    refreshBasePrices() {
      return ensureBasePrices(this.data).then((status) => {
        if (status === 'ok' && this.isConnected) {
          const kept = new Set(this.pairs.map((p) => p.skiVariant.id));
          this.pairs = presetPairs(this.data, this.preset).filter((p) => kept.has(p.skiVariant.id));
          this.renderAll();
        } else if (status === 'error') {
          this.track('error', { stage: 'base_prices', detail: 'unavailable' });
        }
        return status;
      });
    }

    disconnectedCallback() {
      clearTimeout(this._errorTimer);
    }

    track(method, params) {
      if (!this.tracker) return undefined;
      try {
        return this.tracker[method](typeof params === 'function' ? params() : params);
      } catch {
        return undefined;
      }
    }

    // CFG-17 as in the configurator: with the pending option the mounting
    // question starts answered ("Sohlenlänge reiche ich nach"). The panel is
    // always on screen here, so the default counts straight away.
    applyMountDefault() {
      this.mountDefaulted = false;
      if (!this.data.mountStep || this.data.mountPendingEnabled !== true) return;
      if (this.mountChoice != null) return;
      this.mountChoice = 'mounted';
      this.fitToBoot = FIT_PENDING_VALUE;
      this.mountDefaulted = true;
    }

    fitActive() {
      return this.fitToBoot;
    }

    mountAnswer() {
      const pending = isFitPending(this.fitActive());
      return {
        choice: pending ? (this.mountDefaulted ? 'pending_default' : FIT_PENDING_VALUE) : this.mountChoice,
        fitToBoot: false,
        bslGiven: this.bootSoleLength != null,
      };
    }

    enableControls() {
      this.querySelectorAll('[data-mount-option], [data-bsl-input], [data-cta], [data-copy-link]').forEach((el) => { el.disabled = false; });
      // Length pills are enabled per pair in renderLengths.
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const pill = event.target.closest('[data-length-pill]');
        if (pill) return this.onLength(pill);
        const choice = event.target.closest('[data-choice-pill]');
        if (choice) return this.onChoice(choice);
        const cta = event.target.closest('[data-cta]');
        if (cta) return this.onAddToCart(cta);
      });
      this.addEventListener('change', (event) => {
        const option = event.target.closest('[data-mount-option]');
        if (option) return this.onMountChoice(option.value);
        const bsl = event.target.closest('[data-bsl-input]');
        if (bsl) return this.onBslInput(bsl);
      });
      this.addEventListener('input', (event) => {
        const bsl = event.target.closest('[data-bsl-input]');
        if (bsl) return this.onBslInput(bsl);
      });
    }

    // The cart selection for a pair: the pair's fitted parts, and on every
    // sizeChoice step the customer's pick, or nothing until there is one.
    composeSelections(pair) {
      const next = { ...pair.selections };
      this.choiceSteps.forEach((stepHandle) => {
        if (this.choices[stepHandle] != null) next[stepHandle] = this.choices[stepHandle];
        else delete next[stepHandle];
      });
      return next;
    }

    choiceProduct(stepHandle) {
      const item = this.preset.products.find((p) => p.stepHandle === stepHandle);
      const step = this.data.steps.find((s) => s.handle === stepHandle);
      return step?.products?.find((p) => p.handle === item?.productHandle) || null;
    }

    choiceOnOffer(stepHandle, variantId) {
      return choiceVariants(this.choiceProduct(stepHandle)).some((v) => String(v.id) === String(variantId));
    }

    choicesComplete() {
      return this.choiceSteps.every((stepHandle) => this.selections[stepHandle] != null);
    }

    // Prices while a size is still open: the pair's default size stands in
    // (the parts cost the same in every size), so the total is the set's.
    priceSelections() {
      const pair = this.selectedPair();
      return pair ? { ...pair.selections, ...this.selections } : this.selections;
    }

    selectedPair() {
      if (!this.skiStep || this.selections[this.skiStep] == null) return null;
      return this.pairs.find((p) => p.skiVariant.id === this.selections[this.skiStep]) || null;
    }

    onLength(pill) {
      const pair = this.pairs.find((p) => String(p.skiVariant.id) === String(pill.dataset.variantId));
      if (!pair) return;
      const replace = this.selections[this.skiStep] != null;
      this.selections = this.composeSelections(pair);
      this.track('select', () => ({ stepHandle: this.skiStep, variantId: String(pair.skiVariant.id), via: 'length', replace }));
      this.syncUrl();
      this.showError('');
      this.renderAll();
    }

    onChoice(pill) {
      const stepHandle = pill.dataset.step;
      const variantId = Number(pill.dataset.variantId);
      if (!this.choiceSteps.includes(stepHandle) || !this.choiceOnOffer(stepHandle, variantId)) return;
      const replace = this.choices[stepHandle] != null;
      this.choices[stepHandle] = variantId;
      const pair = this.selectedPair();
      if (pair) this.selections = this.composeSelections(pair);
      this.track('select', () => ({ stepHandle, variantId: String(variantId), via: 'size', replace }));
      this.syncUrl();
      this.showError('');
      this.renderAll();
    }

    onMountChoice(value) {
      this.mountDefaulted = false;
      if (value === FIT_PENDING_VALUE) {
        this.mountChoice = 'mounted';
        this.fitToBoot = FIT_PENDING_VALUE;
      } else {
        this.mountChoice = value;
        if (isFitPending(this.fitToBoot)) this.fitToBoot = false;
      }
      this.track('mount', () => this.mountAnswer());
      this.syncUrl();
      this.showError('');
      this.renderAll();
    }

    onBslInput(input) {
      this.mountDefaulted = false;
      const raw = input.value.trim();
      this.bootSoleInput = input.value;
      const error = this.querySelector('[data-bsl-error]');
      if (raw === '') {
        this.bootSoleLength = null;
        if (error) error.hidden = this.mountChoice !== 'mounted';
      } else {
        const value = Number(raw);
        const valid = Number.isInteger(value) && value >= BSL_MIN && value <= BSL_MAX;
        this.bootSoleLength = valid ? value : null;
        if (error) error.hidden = valid;
      }
      input.setAttribute('aria-invalid', this.mountChoice === 'mounted' && this.bootSoleLength == null && !isFitPending(this.fitToBoot) ? 'true' : 'false');
      this.syncUrl();
      this.renderAll();
    }

    // The address carries the chosen length (and a touched mounting answer)
    // so the page can be shared and "Set anpassen" can hand everything over.
    syncUrl() {
      const url = new URL(location.href);
      this.data.steps.forEach((step) => url.searchParams.delete(step.param));
      ['mount', 'bsl', 'fit'].forEach((key) => url.searchParams.delete(key));
      new URLSearchParams(paramsFromSelections(this.data, { ...this.choices, ...this.selections }, this.skipped)).forEach((value, key) => url.searchParams.set(key, value));
      if (!this.mountDefaulted) {
        new URLSearchParams(mountParams(this.data, this.mountChoice, this.bootSoleLength, this.fitActive())).forEach((value, key) => url.searchParams.set(key, value));
      }
      history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }

    customizeHref() {
      const base = this.preset.containerUrl || '';
      if (!base) return null;
      const url = new URL(base, location.origin);
      new URLSearchParams(paramsFromSelections(this.data, this.selections, this.skipped)).forEach((value, key) => url.searchParams.set(key, value));
      if (!this.mountDefaulted) {
        new URLSearchParams(mountParams(this.data, this.mountChoice, this.bootSoleLength, this.fitActive())).forEach((value, key) => url.searchParams.set(key, value));
      }
      url.searchParams.set('cfg_entry', 'preset');
      if (this.preset.handle) url.searchParams.set('cfg_preset', this.preset.handle);
      return `${url.pathname}${url.search}`;
    }

    money(cents) {
      const span = document.createElement('span');
      span.innerHTML = formatMoney(cents, this.data.moneyFormat);
      return span.textContent;
    }

    complete() {
      return this.selectedPair() != null
        && this.choicesComplete()
        && isComplete(this.data, this.selections, this.mountChoice, this.bootSoleLength, this.skipped, this.fitToBoot);
    }

    renderAll() {
      this.renderLengths();
      this.renderChoices();
      this.renderParts();
      this.renderFitLine();
      this.renderTotals();
      this.renderMount();
      this.renderCta();
      this.renderCustomize();
      this.renderPickup();
      this.renderGuides();
      this.track('tick', () => ({
        currentStep: null, trigger: null, panel: null, deferred: false,
        complete: this.complete(), selections: this.selections,
        totals: computeTotals(this.data, this.selections),
        mount: this.data.mountStep ? this.mountAnswer() : null,
      }));
    }

    renderChoices() {
      this.querySelectorAll('[data-choice-pill]').forEach((pill) => {
        const onOffer = this.choiceOnOffer(pill.dataset.step, pill.dataset.variantId);
        const chosen = onOffer && String(this.choices[pill.dataset.step]) === String(pill.dataset.variantId);
        pill.disabled = !onOffer;
        pill.classList.toggle('is-soldout', !onOffer);
        pill.setAttribute('aria-pressed', String(chosen));
        pill.classList.toggle(SKI_LENGTH_SELECTED, chosen);
      });
    }

    renderLengths() {
      const selected = this.selections[this.skiStep];
      this.querySelectorAll('[data-length-pill]').forEach((pill) => {
        const pair = this.pairs.find((p) => String(p.skiVariant.id) === String(pill.dataset.variantId));
        pill.disabled = !pair;
        pill.classList.toggle('is-soldout', !pair);
        pill.setAttribute('aria-pressed', String(Boolean(pair) && String(selected) === String(pill.dataset.variantId)));
        pill.classList.toggle(SKI_LENGTH_SELECTED, Boolean(pair) && String(selected) === String(pill.dataset.variantId));
        if (!pair) pill.title = this.data.strings.lengthUnavailable || '';
        else pill.removeAttribute('title');
      });
      const none = this.querySelector('[data-lengths-none]');
      if (none) none.hidden = this.pairs.length > 0;
    }

    partLabel(stepHandle, product, variant) {
      const s = this.data.strings;
      if (variant?.brakeWidthMm != null) return (s.partBrake || '@mm@ mm').replace('@mm@', variant.brakeWidthMm);
      // A jacket-led set has sizes, not lengths, on its first part.
      if (stepHandle === this.skiStep && parseLengthCm(variant?.title) != null) return (s.partLength || '@cm@ cm').replace('@cm@', variant?.title ?? '');
      return (s.partSize || '@size@').replace('@size@', variant?.displayTitle || variant?.title || '');
    }

    renderParts() {
      // A set price (2026-10-05) is shown as each part's share, so the part
      // lines add up to the total and match the cart lines.
      // A size still open is priced at its default size (priceSelections).
      const shown = this.priceSelections();
      const charges = componentCharges(this.data, shown);
      this.querySelectorAll('[data-part]').forEach((row) => {
        const stepHandle = row.dataset.part;
        const isChoice = this.choiceSteps.includes(stepHandle);
        const picked = isChoice ? this.choices[stepHandle] : null;
        const variantId = shown[stepHandle] ?? picked;
        const match = variantId == null ? null : findVariant(this.data, stepHandle, variantId);
        const label = row.querySelector('[data-part-variant]');
        const price = row.querySelector('[data-part-price]');
        const compare = row.querySelector('[data-part-compare]');
        if (!match) {
          if (label) label.textContent = (isChoice ? this.data.strings.partChoose : this.data.strings.partPending) || '';
          row.classList.remove('is-resolved');
          return;
        }
        row.classList.toggle('is-resolved', !isChoice || picked != null);
        if (label) {
          label.textContent = isChoice && picked == null
            ? (this.data.strings.partChoose || '')
            : this.partLabel(stepHandle, match.product, match.variant);
        }
        const charge = charges.items.find((item) => item.step.handle === stepHandle);
        const charged = charge ? charge.cents : chargedCents(match.product, match.variant);
        const compareAt = compareAtCents(match.variant);
        if (price) price.textContent = this.money(charged);
        if (compare) {
          compare.hidden = !(compareAt > charged);
          compare.textContent = compareAt > charged ? this.money(compareAt) : '';
        }
      });
    }

    renderFitLine() {
      const line = this.querySelector('[data-fit-line]');
      if (!line) return;
      const pair = this.selectedPair();
      if (!pair) {
        line.hidden = true;
        return;
      }
      // Sizes the customer picks are no "passend" line: only fitted parts.
      const parts = this.preset.products.slice(1).filter((item) => !this.choiceSteps.includes(item.stepHandle)).map((item) => {
        const match = findVariant(this.data, item.stepHandle, this.selections[item.stepHandle]);
        if (!match) return null;
        return `${shortProductTitle(match.product.title)} ${this.partLabel(item.stepHandle, match.product, match.variant)}`;
      }).filter(Boolean);
      line.hidden = parts.length === 0;
      line.textContent = (this.data.strings.fitLine || '@parts@').replace('@parts@', parts.join(', '));
    }

    renderTotals() {
      const total = this.querySelector('[data-total]');
      const compare = this.querySelector('[data-compare]');
      const savings = this.querySelector('[data-savings]');
      const from = this.querySelector('[data-price-from]');
      const chip = this.querySelector('[data-set-price-chip]');
      // The chip marks a set price (2026-10-05) from the first paint on: the
      // "ab" price before a length is chosen is that price too.
      if (chip) chip.hidden = !(this.preset?.setPriceCents > 0 && this.pairs.length);
      const pair = this.selectedPair();
      if (!pair) {
        const cheapest = presetFromCents(this.pairs);
        if (total) total.textContent = cheapest == null ? '' : this.money(cheapest);
        if (from) from.hidden = cheapest == null;
        if (compare) compare.hidden = true;
        if (savings) savings.hidden = true;
        return;
      }
      const totals = computeTotals(this.data, this.priceSelections());
      if (from) from.hidden = true;
      if (total) total.textContent = this.money(totals.chargedCents);
      if (compare) {
        compare.hidden = !(totals.compareAtCents > totals.chargedCents);
        compare.textContent = compare.hidden ? '' : this.money(totals.compareAtCents);
      }
      if (savings) {
        savings.hidden = !(totals.savingsCents > 0);
        savings.textContent = savings.hidden ? '' : (this.data.strings.setSavings || '@amount@').replace('@amount@', this.money(totals.savingsCents));
      }
    }

    setBslOpen(open) {
      const wrap = this.querySelector('[data-bsl-block]');
      if (!wrap) return;
      wrap.classList.toggle('is-open', open);
      wrap.style.height = open ? 'auto' : '0px';
      wrap.style.visibility = open ? 'visible' : 'hidden';
    }

    renderMount() {
      if (!this.data.mountStep) return;
      const pending = this.mountChoice === 'mounted' && isFitPending(this.fitActive());
      this.querySelectorAll('[data-mount-option]').forEach((r) => {
        if (r.value === FIT_PENDING_VALUE) r.checked = pending;
        else r.checked = r.value === this.mountChoice && !pending;
      });
      this.setBslOpen(this.mountChoice === 'mounted' && !pending);
      // CFG-20: the chosen option's hint line (shared panel markup).
      const shown = pending ? FIT_PENDING_VALUE : this.mountChoice;
      this.querySelectorAll('[data-mount-hint]').forEach((hint) => { hint.hidden = hint.dataset.mountHint !== shown; });
      const input = this.querySelector('[data-bsl-input]');
      if (input && document.activeElement !== input) input.value = this.bootSoleInput;
      const pendingDesc = this.querySelector('[data-pending-desc]');
      if (pendingDesc) {
        const drilled = selectedSkiProduct(this.data, this.selections)?.drilled === true;
        const copy = drilled ? this.data.strings.pendingDescDrilled : this.data.strings.pendingDesc;
        if (copy && pendingDesc.textContent !== copy) pendingDesc.textContent = copy;
      }
    }

    renderCta() {
      const cta = this.querySelector('[data-cta]');
      if (!cta) return;
      const ready = BundleCart.isReady() && this.complete() && !this.adding;
      cta.disabled = !ready;
      const label = cta.querySelector('[data-cta-label]') || cta;
      const pair = this.selectedPair();
      if (!pair) label.textContent = this.data.strings.ctaChooseLength || label.textContent;
      else if (!this.choicesComplete()) label.textContent = this.data.strings.ctaChooseSizes || label.textContent;
      // A set without a mounting step (Langlauf, Bekleidung) has no question to answer.
      else if (this.data.mountStep && !isMountAnswered(this.mountChoice, this.bootSoleLength, this.fitToBoot)) label.textContent = this.data.strings.ctaAnswerMount || label.textContent;
      else label.textContent = this.data.strings.ctaComplete || label.textContent;
    }

    renderCustomize() {
      const link = this.querySelector('[data-customize]');
      if (!link) return;
      const href = this.customizeHref();
      if (href) link.setAttribute('href', href);
    }

    renderPickup() {
      const pair = this.selectedPair();
      this.querySelectorAll('pickup-availability[data-pickup-step]').forEach((el) => {
        const variantId = this.selections[el.dataset.pickupStep];
        if (variantId == null || !pair) {
          el.hidden = true;
          return;
        }
        el.hidden = false;
        if (String(el.dataset.variantId) !== String(variantId)) {
          el.dataset.variantId = String(variantId);
          if (typeof el.fetchAvailability === 'function') el.fetchAvailability(variantId);
        }
      });
    }

    // Size help (2026-10-26): each pick's drawer (size-guide.js) reads its
    // "Aktuelle Auswahl" from the JSON in its fieldset, as the product page's
    // drawer reads product-info. Written on change only; the drawer re-reads
    // it on open, so one defined later still finds the current pick.
    renderGuides() {
      this.querySelectorAll('[data-size-guide-scope][data-guide-step]').forEach((scope) => {
        const stepHandle = scope.dataset.guideStep;
        const variantId = stepHandle === this.skiStep ? this.selections[stepHandle] : this.choices[stepHandle];
        const match = variantId == null ? null : findVariant(this.data, stepHandle, variantId);
        const json = JSON.stringify(match ? {
          id: match.variant.id, title: match.variant.title, options: [match.variant.title],
          available: match.variant.available, price: match.variant.priceCents,
        } : null);
        const holder = scope.querySelector('[data-selected-variant]');
        if (!holder || holder.textContent === json) return;
        holder.textContent = json;
        const guide = scope.querySelector('pdp-size-guide');
        if (guide?.initialized && typeof guide.syncSelection === 'function') guide.syncSelection();
      });
    }

    announce(message) {
      const region = this.querySelector('[data-live-region]');
      if (region) region.textContent = message;
    }

    showError(message) {
      const box = this.querySelector('[data-error]');
      if (box) {
        box.textContent = message;
        box.hidden = !message;
      }
      if (message) this.announce(message);
    }

    addErrorMessage(error) {
      const strings = this.data.strings;
      if (error?.stage === 'add' && error.status === 422) {
        const overlap = error.context?.overlap || 0;
        const selected = this.data.steps.filter((step) => this.selections[step.handle] != null).length;
        // Every part already held: this very set is in the cart (CFG-23).
        if (overlap > 0 && overlap >= selected && strings.errorAddInCartFull) return strings.errorAddInCartFull;
        if (overlap > 0 && strings.errorAddInCart) return strings.errorAddInCart;
        if (strings.errorAddStock) return strings.errorAddStock;
      }
      return strings.errorAdd;
    }

    // The configurator's add sequence (bundle-configurator.js onAddToCart),
    // kept in step by tests/bundle-set-add-flow-contract.test.mjs.
    async onAddToCart(cta, via = 'set_page') {
      if (this.adding || !this.complete()) return;
      this.adding = true;
      cta.disabled = true;
      cta.classList.add('loading');
      let added = false;
      try {
        if (!BundleCart.isReady()) {
          this.track('atcResult', { result: 'not_ready', via });
          this.showError(this.data.strings.notConfigured);
          return;
        }
        await this.refreshBasePrices();
        const stale = await BundleCart.recheckAvailability(this.data, this.selections);
        const staleChoice = stale.find((stepHandle) => this.choiceSteps.includes(stepHandle));
        if (staleChoice && stale.every((stepHandle) => this.choiceSteps.includes(stepHandle))) {
          // Only a picked size is gone: that size goes, the length stays.
          const match = findVariant(this.data, staleChoice, this.selections[staleChoice]);
          stale.forEach((stepHandle) => {
            const gone = findVariant(this.data, stepHandle, this.selections[stepHandle]);
            if (gone) gone.variant.available = false;
            delete this.choices[stepHandle];
          });
          this.selections = this.composeSelections(this.selectedPair());
          this.track('atcResult', () => ({ result: 'unavailable', via, totals: computeTotals(this.data, this.priceSelections()) }));
          this.syncUrl();
          this.showError((this.data.strings.errorChoiceUnavailable || this.data.strings.errorUnavailable).replace('@title@', match?.product.title || ''));
          return;
        }
        if (stale.length) {
          const match = findVariant(this.data, stale[0], this.selections[stale[0]]);
          this.track('atcResult', () => ({ result: 'unavailable', via, totals: computeTotals(this.data, this.selections) }));
          // One part is gone: the whole length is gone with it (no partial sets).
          this.pairs = this.pairs.filter((p) => p.skiVariant.id !== this.selections[this.skiStep]);
          this.selections = {};
          this.syncUrl();
          this.showError(this.data.strings.errorUnavailable.replace('@title@', match?.product.title || ''));
          return;
        }
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sectionsArgs = cart
          ? { sections: cart.getSectionsToRender().map((s) => s.id), sectionsUrl: location.pathname }
          : {};
        cart?.setActiveElement?.(cta);
        const response = await BundleCart.addBundle({
          data: this.data, selections: this.selections,
          mountChoice: this.mountChoice, bootSoleLength: this.bootSoleLength,
          fitToBoot: this.fitActive(),
          mountBy: this.mountDefaulted ? MOUNT_BY.auto : MOUNT_BY.customer,
          ...sectionsArgs,
        });
        const integrity = await BundleCart.enforceCartIntegrity();
        if (integrity.cleaned) {
          this.track('atcResult', () => ({ result: 'integrity_cleaned', via, totals: computeTotals(this.data, this.selections) }));
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-set', cartData: integrity.cart });
          this.showError(this.data.strings.errorAdd);
          return;
        }
        added = true;
        this.track('atcResult', () => ({ result: 'success', via, totals: computeTotals(this.data, this.selections) }));
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-set', cartData: response });
        if (cart) {
          const line = response.items?.[0];
          cart.renderContents({ ...response, key: line?.key, id: line?.id });
        }
        this.showError('');
      } catch (error) {
        if (!added) this.track('atcResult', () => ({ result: 'error', via, stage: error?.stage || 'unknown', detail: errorDetail(error), totals: computeTotals(this.data, this.selections) }));
        this.showError(this.addErrorMessage(error));
      } finally {
        this.adding = false;
        cta.classList.remove('loading');
        this.renderAll();
      }
    }
  });
}
